// Modules
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import axios from 'axios';
import cx from 'classnames';
import uniqueId from 'lodash/uniqueId';
import isEqual from 'lodash/isEqual';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
// Components
import PreviewList from './Preview/PreviewList';
import PreviewSingleImage from './Preview/PreviewSingleImage';
import ModalCropper from '../Cropper/CropperModal';
// Helpers
import {
  MEDIA_CONTENT_TYPE,
  getAssetsActionUrl,
  VIDEO_STORAGE,
  MEDIA_STATUS
} from '../../constants';
import checkAgent from '../../utils/checkUserAgent';
import { MAX_CHUNK_SIZE, MAX_IMAGE_SIZE, MAX_VIDEO_SIZE } from './helpers';
import { getCompanyImagePublicId } from '../../utils/companyImages';
import {
  buildVideoFields, buildImageFields, computeMediaIsPending, computeMediaHasFailed
} from '../../utils/normalizeMediaStatus';
// Styles
import * as styles from './UploadAssetWidget.scss';

const videoSources = { internal: 'internal' };

const formatContentFromResponse = ({ data }, { _id, angle = '0', relativeAngle = '0', contentType, type }) => {
  const isVideo = contentType === MEDIA_CONTENT_TYPE.VIDEO;

  if (isVideo && data.storage === VIDEO_STORAGE.R2) {
    return {
      ...buildVideoFields(data),
      uploadInProgress: false,
      angle,
      relativeAngle,
      contentType,
      _id,
      source: videoSources.internal,
      type,
      name: data.name
    };
  }

  const source = isVideo && videoSources.internal;

  return {
    thumbnail: data.url,
    full: data.url,
    public_id: data.public_id,
    link: data.public_id,
    id: data.public_id,
    imageId: data.image_id,
    indexIdentifier: data.indexIdentifier,
    uploadInProgress: false,
    angle,
    relativeAngle,
    contentType,
    _id,
    source,
    type,
    name: data.name,
    status: data.status,
    error: data.error || data.status === MEDIA_STATUS.FAILED
  };
};

export default class UploadAssetWidget extends Component {
  static convertLinkToImageObject = (link = '') => ({ full: link, thumbnail: link });

  static formatInitialImages = (data = []) => data.map((image) => {
    const {
      public_id,
      description,
      angle,
      relativeAngle,
      full,
      thumbnail,
      status,
      error,
      imageId,
      image_id: rawImageId,
      _id // = uniqueId('upload_image_')
    } = image;
    // Initial angle calculation
    const imageAngle = UploadAssetWidget.getAngle({ angle, full });

    const linkId = getCompanyImagePublicId({ public_id, full });

    return ({
      thumbnail,
      full,
      link: linkId,
      id: public_id,
      imageId: imageId || rawImageId,
      description,
      angle: imageAngle,
      relativeAngle: relativeAngle || 0,
      contentType: MEDIA_CONTENT_TYPE.IMAGE,
      status,
      error,
      _id: _id || `upload_image_${linkId}`
    });
  });

  static modifyImagesForPreview = images => images.map(image => ({ ...image }));

  static getAngle = (image) => {
    let angle;

    if (image.angle || image.angle === 0) {
      angle = image.angle;
    } else if (image.full) {
      const arrayFromImageLink = image.full.split('/');
      const imageParams = arrayFromImageLink[arrayFromImageLink.length - 2];
      const imageParamsArray = imageParams.split(',');
      const angleParam = imageParamsArray.filter(item => (item.indexOf('a_') === 0));
      angle = (angleParam.length) ? +angleParam[0].split('_').pop() : 0;
    } else {
      angle = 0;
    }

    return angle;
  };

  state = {
    images: this.props.initialImages || [],
    videos: this.props.initialVideos || [],
    imagesDeletedDuringUpload: [],
    uploadInProgress: false,
    isTabletDevice: checkAgent.isMobileOrTablet(navigator.userAgent),
    isOpenModalCropper: false,
    imgCropp: null,
    isCroppImg: null,
    isCropping: false,
    contentLimit: {
      images: this.props.limitImages || 25,
      videos: 1
    }
  };

  componentDidMount = () => {
    this.widget.addEventListener('click', this.handleClick, true);
    window.addEventListener('dragover', this.handleDrag);
    window.addEventListener('mouseover', this.handleMouseOver);
    this.widget.addEventListener('selectstart', this.handleSelect);
  };

  componentWillReceiveProps(nextProps, nextContext) {
    if (!isEqual(nextProps.initialImages, this.props.initialImages)) {
      this.setState({
        images: nextProps.initialImages
      });

      this.props.updateContent(nextProps.initialImages, 'images');
    }
    if (!isEqual(nextProps.initialVideos, this.props.initialVideos)) {
      this.setState({ videos: nextProps.initialVideos || [] });
      this.props.updateContent(nextProps.initialVideos, 'videos');
    }
  }

  componentWillUnmount = () => {
    this.widget.removeEventListener('click', this.handleClick, true);
    window.removeEventListener('dragover', this.handleDrag);
    window.removeEventListener('mouseover', this.handleMouseOver);
    this.widget.removeEventListener('selectstart', this.handleSelect);
  };

  onContentDrop = (files) => {
    if (this.props.multiple) {
      const formattedFiles = files.map(this.formatContentBeforeUpload);
      const sortedFiles = this.sortFiles(formattedFiles);

      this.setState(
        {
          images: [...this.state.images, ...sortedFiles.images],
          videos: [...this.state.videos, ...sortedFiles.videos]
        },
        () => this.uploadContent([...sortedFiles.images, ...sortedFiles.videos])
      );
    } else {
      const formatted = this.formatContentBeforeUpload(files[0]);

      formatted && this.setState({
        nameImage: formatted.name,
        images: [formatted]
      }, () => this.uploadContent([formatted]));
    }
  };

  getStateKey = (file) => {
    const { contentType } = file;
    const type = contentType || (file.type && file.type.split('/')[0]);

    const isVideo = (type === MEDIA_CONTENT_TYPE.VIDEO);
    return (isVideo) ? 'videos' : 'images';
  };

  getPublicId = image => (
    (image && image.public_id)
      ? image.public_id
      : image.full.split('/').pop()
  );

  formatContentBeforeUpload = (file) => {
    const type = file.type.split('/');
    file.contentType = type[0];
    file.uploadInProgress = true;
    file._id = uniqueId('upload_image_');
    if (file.contentType === 'video' && file.size > MAX_VIDEO_SIZE) {
      this.showLimitMessage('videoSizeMessage');
      return null;
    }
    if (!this.props.multiple && (file.contentType === 'image') && (file.size > MAX_IMAGE_SIZE)) {
      this.showLimitMessage('imageSizeMessage');
      return null;
    }
    return file;
  };

  handleCropp = (file) => {
    const formated = this.formatContentBeforeUpload(file);
    this.setState({ images: [file] });

    this.closeModalCropper();
    this.croppImg(true);
    this.sendFile(formated);
  }

  showLimitMessage = (errorKey) => this.props.openMessageModal({
    message: (
      <div className={cx(styles.limitMessage, 'successMessage')}>
        <p>{this.props.rootTranslate('uploadWidget', errorKey)}</p>
      </div>
    ),
    isCloseButtonInvisible: true
  });

  sortFiles = (files = []) => files.reduce((acc, item) => {
    const stateKey = this.getStateKey(item);

    if (this.state[stateKey].length + acc[stateKey].length < this.state.contentLimit[stateKey]) {
      if ((item.contentType === 'video') && (item.size > MAX_VIDEO_SIZE)) {
        this.showLimitMessage('videoSizeMessage');
        return acc;
      }
      if (item.contentType !== 'video' && item.size > MAX_CHUNK_SIZE) {
        this.showLimitMessage('fileSizeMessage');
        return acc;
      }
      if ((item.contentType === 'image') && (item.size > MAX_IMAGE_SIZE)) {
        this.showLimitMessage('imageSizeMessage');
        return acc;
      }
      acc[stateKey].push(item);
    } else {
      this.showLimitMessage(this.props.isRaw ? 'limitationRawError' : 'limitationError');
    }

    return acc;
  }, { images: [], videos: [] });

  handleSelect = (evt) => {
    evt.preventDefault();
  };

  handleDrag = (event) => {
    event.preventDefault();
    event.dataTransfer.effectAllowed = 'none';
    event.dataTransfer.dropEffect = 'none';
    this.widget.classList.add(styles.dropArea);
  };

  handleMouseOver = () => {
    if (this.widget.classList.contains(styles.dropArea)) {
      this.widget.classList.remove(styles.dropArea);
    }
  };

  sendFile = (file) => {
    const { index, _id, contentType, name, type } = file;

    const uploadProgressBar = document.querySelector(`#uploadProgressBar_${_id}`);
    const urlType = type === 'application/pdf' ? 'raw' : contentType;
    const uploadUrl = getAssetsActionUrl(urlType);
    const urlAdditional = this.props.suspendedUpload ? '/suspend' : '';
    const stateKey = this.getStateKey(file);

    const uploadData = new FormData();

    uploadData.append('file', file);

    return axios.post(`${uploadUrl}${urlAdditional}`, uploadData, {
      timeout: 60000000,
      onUploadProgress: (progressEvent) => {
        if (uploadProgressBar && uploadProgressBar.offsetParent) {
          if (!uploadProgressBar.offsetParent.style.border) {
            uploadProgressBar.offsetParent.style.border = 'solid 1px #cccccc';
          }
          uploadProgressBar.style.width = `${Math.round((progressEvent.loaded * 100.0) / progressEvent.total)}%`;
        }
      },
      transformResponse: (data) => {
        const response = JSON.parse(data);
        response.indexIdentifier = `image_${index}`;
        response.name = name;
        return response;
      }
    })
      .then((response) => {
        const isFileUploaded = get(response, 'data.done') !== false; // important, 'data.done' is not equal only 'false'
        const loadingError = get(response, 'data.error');

        if (!!loadingError) {
          this.showLimitMessage('uploadErrorMessage');
          throw new Error(loadingError.message);
        }

        return isFileUploaded && this.saveUploadedContentToState(file, response);
      })
      .catch(err => this.timeoutUpload(err, _id, stateKey));
  };

  timeoutUpload = (err, contentId, stateKey) => this.setState(
    { [stateKey]: this.state[stateKey].filter(({ _id }) => _id !== contentId) }
  );

  _uploadContent = (files) => Promise.all(files.map(file =>
    this.sendFile(file)
  ));

  uploadContent = (files) => {
    if (files.length) {
      this.setState({ uploadInProgress: true });
      this.props.uploadInProgress(true);

      this._uploadContent(files)
        .then(this.uploadFinish);
    }
  };

  uploadFinish = () => {
    this.setState({ uploadInProgress: false });
    this.props.uploadInProgress(false);
  };

  saveUploadedContentToState = (file, uploadResponse) => {
    const stateKey = this.getStateKey(file);

    const data = [...this.state[stateKey]];
    const fileIndex = findIndex(data, item => item._id === file._id);

    if (fileIndex === -1) {
      console.error('No image with this index');
      return;
    }

    const uploadedContent = formatContentFromResponse(uploadResponse, data[fileIndex]);
    const updatedData = data.map(item => (item._id === file._id) ? uploadedContent : item);

    // A still-pending upload's URL isn't loadable yet - the cropper would just show a broken
    // image. Wait for it to settle (see updateImageProcessingStatus) before opening it.
    if (!this.state.isCroppImg && !computeMediaIsPending(uploadedContent)) {
      this.setState({ imgCropp: updatedData[0].full, isOpenModalCropper: true });
    }
    this.setState({ [stateKey]: updatedData }, () => {
      this.props.contentUploaded(updatedData, uploadedContent, stateKey);
    });
  };

  handleCroppStart = () => {
    this.setState({ isCropping: true });
  };

  handleCroppEnd = () => {
    // set isCropping = false, after crop end event
    requestAnimationFrame(() => {
      this.setState({ isCropping: false });
    });
  };

  hideCropperModal = (evt) => {
    const [image] = this.state.images;

    this.deleteContent(evt, 0, image);
    this.setState({ imgCropp: null, isOpenModalCropper: false });
  }

  handleCropperRequestClose = () => {
    if (this.state.isCropping) {
      return;
    }
    this.hideCropperModal();
  };

  markAssetToDelete = (publicId, contentType = 'image') => {
    this.props.markAssetToDelete && this.props.markAssetToDelete({ publicId, contentType });
  }

  deleteAllContent = () => {
    const imagesShouldBeDeletedPublicId = this.state.images.map(image => (
      (image.public_id)
        ? image.public_id
        : image.full.split('/').pop())
    );
    const videosShouldBeDeletedPublicId = this.state.videos.map(video => (
      (video.public_id)
        ? video.public_id
        : video.full.split('/').pop())
    );

    imagesShouldBeDeletedPublicId.forEach(id => this.markAssetToDelete(id, 'image'));
    videosShouldBeDeletedPublicId.forEach(id => this.markAssetToDelete(id, 'video'));

    this.setState({
      images: [],
      videos: []
    });
    this.props.updateContent([], 'images');
    this.props.updateContent([], 'videos');
  };

  deleteContent = (event, index, contentItem) => {
    event && event.stopPropagation();
    const stateKey = this.getStateKey(contentItem);

    const publicId = (contentItem.full) ? this.getPublicId(contentItem) : '';

    this.markAssetToDelete(publicId, contentItem.contentType);

    this.deleteContentFromStateByIndex(index, stateKey);
    this.setState({ uploadInProgress: false });
    this.croppImg(false);
  };

  deleteContentFromStateByIndex = (index, stateKey) => {
    if (this.state[stateKey].length) {
      const widget = document.querySelector(`.${styles.fileUpload}`);
      widget.classList.add(styles.noTransition);
      const newContent = [...this.state[stateKey]];
      const { _id } = newContent[index];
      newContent.splice(index, 1);

      this.setState({ [stateKey]: newContent }, () => {
        setTimeout(() => {
          widget.classList.remove(styles.noTransition);
          if (!this.props.multiple) {
            return this.props.updateContent(newContent, stateKey);
          }
          return this.props.deleteContentItem(_id, stateKey);
        }, 0);
      });
    }
  };

  closeModalCropper = () => this.setState({ isOpenModalCropper: false });
  croppImg = (status) => this.setState({ isCroppImg: status });

  addDescription = (description, index, file) => {
    const stateKey = this.getStateKey(file);
    const updatedContent = this.state[stateKey].map(image => ({ ...image }));
    updatedContent[index].description = description;

    this.setState({
      [stateKey]: updatedContent
    }, () => {
      this.props.addDescription?.(updatedContent[index]._id, description, stateKey);
    });
  };

  updateVideoProcessingStatus = (_id, statusData = {}) => {
    const updatedVideos = this.state.videos.map(video => (video._id !== _id) ? video : {
      ...video,
      ...buildVideoFields(statusData)
    });

    this.setState({ videos: updatedVideos }, () => {
      this.props.updateAssetStatus?.(updatedVideos, 'videos');
    });
  };

  buildCropperOpenStateOnSettle = (_id, settledImage) => {
    const stillCurrent = this.state.images.some(image => image._id === _id);
    const canOpenCropper = this.props.withCropper
      && stillCurrent
      && !this.state.isCroppImg
      && !this.state.isOpenModalCropper
      && !computeMediaIsPending(settledImage)
      && !computeMediaHasFailed(settledImage);

    return canOpenCropper ? { imgCropp: settledImage.full, isOpenModalCropper: true } : {};
  };

  updateImageProcessingStatus = (_id, statusData = {}) => {
    const updatedImages = this.state.images.map(image => (image._id !== _id) ? image : {
      ...image,
      ...buildImageFields(statusData)
    });
    const settledImage = updatedImages.find(image => image._id === _id);

    this.setState({
      images: updatedImages,
      ...this.buildCropperOpenStateOnSettle(_id, settledImage)
    }, () => {
      this.props.updateAssetStatus?.(updatedImages, 'images');
    });
  };

  handleAssetSettled = (_id, key, statusData) => (key === 'videos'
    ? this.updateVideoProcessingStatus(_id, statusData)
    : this.updateImageProcessingStatus(_id, statusData));

  bindImageStatusHandler = _id => statusData => this.updateImageProcessingStatus(_id, statusData);

  handleClick = (event) => {
    const { images, videos } = this.state;
    if ((images.length || videos.length) && event.target.tagName !== 'SPAN') {
      event.stopPropagation();
    }
  };

  rotateImage = (evt, index, item) => {
    evt.stopPropagation();

    const angle = UploadAssetWidget.getAngle(item);
    const relativeAngle = item.relativeAngle || 0;
    const newAngle = (+angle + 90) % 360;
    const newRelativeAngle = +relativeAngle + 90;

    const newImages = this.state.images.map(image => ({ ...image }));
    const currentImage = newImages[index];

    const imageNode = document.getElementById(`rotated_image_${item._id}`);
    const imageNodeWrapperWidth = imageNode.offsetParent.offsetWidth;

    if (newRelativeAngle % 360 === 90 || newRelativeAngle % 360 === 270) {
      imageNode.style.height = `${imageNodeWrapperWidth}px`;
    } else {
      imageNode.style.height = '';
    }

    if (currentImage.preview) {
      imageNode.dataset.relativeAngle = newRelativeAngle;
    }

    currentImage.angle = newAngle;
    currentImage.relativeAngle = newRelativeAngle;

    this.setState({
      images: newImages
    }, () => {
      if (this.props.multiple) {
        return this.props.rotateImage?.(currentImage._id, newAngle);
      }

      return this.props.updateContent(newImages, 'images');
    });
  };

  sortImagesByOrder = (order) => {
    const orderedImages = order.map(index => this.state.images[index]);
    this.props.updateContent(orderedImages, 'images');
    return orderedImages;
  };

  renderSingleImage = () => {
    const { uploadProgressWrapperClass, uploadProgressClass, uploadProgressTitleClass } = this.props;
    const { images, nameImage } = this.state;

    if (!images || !images.length) {
      return null;
    }

    const [image] = images;

    return (
      <div className={styles.previewWrapper}>
        {!image.preview && !image.uploadInProgress &&
          <PreviewSingleImage
            isTabletDevice={this.state.isTabletDevice}
            deleteFileTitle={this.props.deleteFileTitle || this.props.translate('rotate')}
            rotateImageTitle={this.props.translate('delete')}
            deleteImage={evt => this.deleteContent(evt, 0, image)}
            rotateImage={evt => this.rotateImage(evt, 0, image)}
            index={0}
            image={(image.thumbnail) ? image.thumbnail : image.preview}
            relativeAngle={image.relativeAngle || 0}
            uploadInProgress={!!image.preview}
            id={image._id}
            nameImage={nameImage}
            singleImageClass={this.props.singleImageClass}
            mediaId={image.imageId}
            isPending={computeMediaIsPending(image)}
            hasError={computeMediaHasFailed(image)}
            onMediaStatusChange={this.bindImageStatusHandler(image._id)}
            compact={this.props.compact}
          />
        }
        {(!!image.preview || !!image.uploadInProgress) && (
          <div className={cx(styles.uploadProgressWrapper, uploadProgressWrapperClass)}>
            <div className={cx(styles.title, uploadProgressTitleClass)}>{this.props.translate('loading')}</div>
            <div className={cx(styles.uploadProgress, uploadProgressClass)}>
              <div id={`uploadProgressBar_${image._id}`} />
            </div>
          </div>
        )}
      </div>
    );
  };

  renderPreviewList = () => (
    <PreviewList
      items={this.state.images}
      videos={this.state.videos}
      deleteItem={this.deleteContent}
      onChange={order => this.setState({ images: this.sortImagesByOrder(order) })}
      uploadInProgress={this.state.uploadInProgress}
      translate={this.props.translate}
      isTabletDevice={this.state.isTabletDevice}
      isMobile={this.props.isMobile}
      addDescription={this.addDescription}
      rotateImage={this.rotateImage}
      multiple={this.props.multiple}
      onAssetSettled={this.handleAssetSettled}
    />
  );

  renderPlaceholder = () => {
    const addMore = (!this.props.multiple && (this.state.images.length || this.state.videos.length))
      ? null
      : (<span className={cx(styles.addMore, 'icon icon-clear')} />);
    const descriptionKey = `selectImage${this.props.isMobile ? 'Mobile' : ''}`;

    const content = (this.props.multiple)
      ? (<div className={styles.placeholderMessage}>{addMore}<span>{this.props.translate(descriptionKey)}</span></div>)
      : (<div className={this.props.selectImageWrapper}><i className="icon icon-upload" /><span className={this.props.selectImageClass}>{this.props.selectImageText || this.props.translate('selectImage')}</span></div>);

    const placeHolder = (
      <div className={cx(styles.imagePlaceholder, { [styles.multipleImagePlaceholder]: this.props.multiple })}>
        {this.props.multiple && (
          <div className={styles.placeholderIcons}>
            <span className="icon icon-gallery-image" />
            &nbsp;&nbsp;
            {!this.props.withOutVideo && <span className="icon icon-gallery-video-view" />}
          </div>
        )}
        {content}
      </div>
    );

    return (this.state.images.length || this.state.videos.length) ? addMore : placeHolder;
  };

  renderInfoSection = (isEmpty) => {
    if (!this.props.multiple) { return null; }
    const translate = this.props.rootTranslate('uploadWidget');

    return (
      <span className={styles.infoSection}>
        <span>{this.state.images.length}/{this.state.contentLimit.images}&nbsp;{translate('images')}</span>
        {!this.props.withOutVideo &&
          <span>{this.state.videos.length}/{this.state.contentLimit.videos}&nbsp;{translate('videos')}</span>}
        <span
          className={cx(styles.deleteButton, { [styles.disabled]: isEmpty || this.state.uploadInProgress })}
          onClick={!this.state.uploadInProgress && this.deleteAllContent}
        >
          {translate('deleteAll')}
        </span>
      </span>
    );
  };

  render() {
    const isNotEmpty = !!(this.state.images.length || this.state.videos.length);
    const preview = (this.props.multiple) ? this.renderPreviewList : this.renderSingleImage;
    const withOutVideo = (!this.props.withOutVideo) ? ', video/*' : '';
    const raw = this.props.isRaw ? ', .pdf' : '';
    const mimeType = (this.props.multiple) ? `image/*${withOutVideo}${raw}` : 'image/*';
    const { messageIconClassName } = this.props;

    return (
      <div
        ref={(widget) => { this.widget = widget; }}
        className={cx(
          styles.fileUpload,
          { [styles.singleUpload]: !this.props.multiple },
          { [styles.invalid]: this.props.isInvalid },
          { [styles.uploadInProgress]: !!this.state.images.length && !this.props.multiple },
          this.props.className
        )}
        style={{ cursor: isNotEmpty ? 'default' : 'pointer' }}
      >
        <Dropzone
          onDrop={this.onContentDrop}
          multiple={this.props.multiple}
          accept={mimeType}
          style={{ width: '100%' }}
        >
          {preview()}

          {this.renderPlaceholder()}
        </Dropzone>

        {this.renderInfoSection(!isNotEmpty)}

        {(this.props.multiple) && (
          <div className={styles.dragDropTitle}>{this.props.translate('dragToAdd')}</div>
        )}
        {!this.props.multiple &&
          <div className={cx(styles.messageIcon, messageIconClassName)}>
            {this.props.translate('iconSizeMessage')}
          </div>
        }
        {this.props.withCropper &&
          <ModalCropper
            openModal={this.state.isOpenModalCropper}
            hideCropperModal={this.hideCropperModal}
            img={this.state.imgCropp}
            handleCropp={this.handleCropp}
            handleCroppStart={this.handleCroppStart}
            handleCroppEnd={this.handleCroppEnd}
            translate={this.props.translate}
            onRequestClose={this.handleCropperRequestClose}
          />
        }
      </div>
    );
  }
}

UploadAssetWidget.propTypes = {
  translate: PropTypes.func,
  userToken: PropTypes.string,
  rootTranslate: PropTypes.func,
  withOutVideo: PropTypes.bool,
  multiple: PropTypes.bool,
  isMobile: PropTypes.bool,
  className: PropTypes.string,
  initialImages: PropTypes.array,
  initialVideos: PropTypes.array,
  uploadInProgress: PropTypes.func,
  uploadImageTimeout: PropTypes.func,
  updateContent: PropTypes.func,
  contentUploaded: PropTypes.func,
  deleteContentItem: PropTypes.func,
  markAssetToDelete: PropTypes.func,
  suspendedUpload: PropTypes.bool,
  rotateImage: PropTypes.func,
  addDescription: PropTypes.func,
  updateAssetStatus: PropTypes.func,
  openMessageModal: PropTypes.func,
  isInvalid: PropTypes.bool,
  isRaw: PropTypes.bool,
  withCropper: PropTypes.bool,
  limitImages: PropTypes.number,
  messageIconClassName: PropTypes.string,
  selectImageText: PropTypes.func,
  uploadProgressWrapperClass: PropTypes.string,
  uploadProgressClass: PropTypes.string,
  singleImageClass: PropTypes.string,
  deleteFileTitle: PropTypes.func,
  selectImageClass: PropTypes.string,
  selectImageWrapper: PropTypes.string,
  uploadProgressTitleClass: PropTypes.string,
  compact: PropTypes.bool
};
